import { ReceiptTarget, TransactionTypes } from '@packages/utils/enums'
import { accessTokenStore } from '@packages/stores'
import { get } from 'svelte/store'
import { configStore } from '@src/stores/config'
import { getClientTokenConfig } from '@lib/clients'
import { initSuiClient } from '@src/libs/sui'
import type { TPaymentAsset } from './payment.constants'
import { pageOptsStore, pageStore, userStore } from '@packages/stores'
import { Pages } from '@packages/utils/enums'
import { analytics } from '@lib/mixpanel/mixpanel'
import { waitVideoEnded } from '@components/progress/ProgressVideo.service'
import { getAddress } from '@src/passport/blockchain'
import type { TPaymentReturn } from '@packages/utils/types'
import { validateOrderItems, validateTxs } from '@lib/orders'
import { dispatch, PassportEvents } from '@lib/events/events'

export const requestSuiReceipt = async ({
  paymentIntentId,
  title,
  digest,
  subtitle,
  amount,
  currency,
}: {
  paymentIntentId?: string
  digest?: string
  amount?: number
  currency?: string
  title: string
  subtitle: string
}) => {
  const { credenza, receiptTarget } = get(configStore)

  const targets = !receiptTarget
    ? [ReceiptTarget.EMAIL]
    : receiptTarget.filter((target) => {
        const user = get(userStore)
        if (!user) return
        return user[target]
      })

  let successCount = 0

  for (const target of targets) {
    const result = await fetch(`${credenza.suiApiUrl}/tokens/receipt`, {
      method: 'POST',
      body: JSON.stringify({
        ...(paymentIntentId ? { pi_id: paymentIntentId } : {}),
        ...(digest ? { digest } : {}),
        ...(amount ? { amount } : {}),
        ...(currency ? { currency } : {}),
        target,
        title,
        subtitle,
        source: getClientTokenConfig().name,
      }),
      headers: {
        Authorization: `Bearer ${get(accessTokenStore)}`,
        'Content-Type': 'application/json',
      },
    })

    if (result.ok) {
      successCount++
    } else {
      console.warn(`Failed to send receipt to ${target}:`, result.status)
    }
  }

  return successCount > 0
}

export const afterPaymentProcess = async ({
  res,
  priceCents,
  purchaseConfig,
}: {
  res: TPaymentReturn
  priceCents: number
  purchaseConfig: { assets: TPaymentAsset[]; title: string; subtitle: string }
}) => {
  const config = get(configStore)

  const { chain_txs, items } = res
  const failedOrdersMessage = validateOrderItems(items)
  validateTxs(chain_txs, failedOrdersMessage)

  dispatch(PassportEvents.PAYMENT, { type: TransactionTypes.CARD })
  dispatch(PassportEvents.RECHECK_BALANCE)
  const user = get(userStore)
  if (user) {
    analytics.track('cp_payment_fiat', {
      $email: user.email,
      $phone: user.phone,
      suiNetwork: config.suiNetwork,
      clientId: config.clientId,
      address: await getAddress(),
      location: window.location.href,
      assets: purchaseConfig?.assets,
      total: priceCents / 100,
      description: (purchaseConfig?.title ?? '') + `(${purchaseConfig?.subtitle ?? ''})`,
    })
  }

  await waitVideoEnded()

  if (failedOrdersMessage) {
    pageOptsStore.set({
      title: 'Done!',
      subtitle: 'Your payment was partly successful',
      orders: items,
    })
  }
  pageStore.set(Pages.PAYMENT_RESULT)
}

export const enrichAssetsWithMetadata = async (items: TPaymentReturn['items']) => {
  for (const item of items) {
    const client = await initSuiClient(item?.asset?.packageId as string)

    let metadata = null
    if (item?.asset?.assetId) {
      metadata = await client.CredenzaAssetCollection.getAssetMetadata(+item?.asset?.assetId)
      item.asset.metadata = metadata
    }
  }
}
